import type { Event } from "../../../base/common/events.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import type { TimingSupport } from "./gpuTimingSupport.js";

/**
 * Measuring how long the GPU actually works.
 *
 * ONE implementation for every consumer — the Performance panel's tooltip, the
 * AI tool, and later the render graph. The profiling used to live inside the AI
 * tool, which meant the UI could not ask the same question without a second copy
 * of the query bookkeeping.
 *
 * ON DEMAND, never ambient: WebGL needs a timer query around every draw, which
 * is fine for a second and not fine forever. Nothing is patched until `measure`
 * is called and everything is restored when it returns.
 */

export interface GpuTimingFrame {
    readonly frame: number;
    /** summed GPU time of the draws in this frame, ms */
    readonly gpuMs: number;
    readonly drawCalls: number;
}

export interface GpuTimingResult {
    readonly backend: "webgl" | "webgpu";
    readonly framesMeasured: number;
    readonly avgMs: number;
    readonly minMs: number;
    readonly maxMs: number;
    readonly avgDrawCalls: number;
    /** when the measurement finished (Date.now()) — the UI says how stale it is */
    readonly at: number;
    readonly frames: readonly GpuTimingFrame[];
    /** per-pass detail where the backend gives it; empty when it does not
     *  (WebGPU resolves one timestamp per frame, not per pass) */
    readonly passes: readonly PassSummary[];
}

/** One render pass, averaged over the frames measured. */
export interface PassSummary {
    readonly pass: number;
    /** the scene's name, or "Pass N" when it has none */
    readonly label: string;
    readonly avgMs: number;
    readonly avgDraws: number;
    /** how many measured frames contained this pass */
    readonly frames: number;
    /** uuid of the scene it drew — how the render graph pairs a captured call
     *  with its measurement (names collide, positions shift) */
    readonly sceneId?: string;
}

export const IGpuTimingService = createDecorator<IGpuTimingService>("gpuTimingService");

export interface IGpuTimingService {
    readonly _serviceBrand: undefined;

    /** Whether this page can be timed at all, and if not, why — for a UI that has
     *  to explain itself rather than grey a button out silently. */
    support(): TimingSupport;

    /** Profile `frames` displayed frames. Rejects only on a real failure; an
     *  unsupported renderer resolves to null so a caller can show the reason. */
    measure(frames?: number): Promise<GpuTimingResult | null>;

    /** The last measurement, so a panel can render it without re-profiling. */
    readonly last: GpuTimingResult | null;

    /** A measurement finished (null when nothing could be measured) — views
     *  showing these numbers, like the render graph's pass cards, re-read here
     *  instead of keeping whatever was current when they were built. */
    readonly onDidMeasure: Event<GpuTimingResult | null>;
}
